import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { faArrowRight, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import { LoadingIcon } from "common/loading";
import AddToCalendarModal from "common/modal/add_to_calendar_modal";

// graphql
import GET_APPOINTMENT from "graphql/queries/GetAppointment";
import GET_CURRENT_USER_INFO from "graphql/queries/GetCurrentUserInfo";

// hooks
import useModal from "hooks/useModal";

// types
import { Lesson } from "types";

// styling
import {
  CardFooterButton,
  StackedCardFooterContainer,
  CardFooterNavigationArrow,
  ClassCardFooterWrapper,
} from "./ClassCardStyle";

type TDynamicCardFooterProps = {
  lesson: Lesson;
  gray?: boolean;
};
const DynamicCardFooter = ({ lesson, gray }: TDynamicCardFooterProps) => {
  const { t } = useTranslation("my_classes");
  const navigate = useNavigate();
  const [
    visibleAddToCalendarModal,
    showAddToCalendarModal,
    closeAddToCalendarModal,
  ] = useModal();

  const { loading, data } = useQuery(GET_APPOINTMENT, {
    variables: { id: lesson.id },
    fetchPolicy: "network-only",
  });

  const { loading: loadingCurrentUser, data: currentUser } = useQuery(
    GET_CURRENT_USER_INFO
  );

  if (loading || loadingCurrentUser)
    return (
      <div>
        <LoadingIcon />
      </div>
    );

  const { appointment } = data.classDetails;
  const { webcalUrl } = currentUser.me;

  const navigateToCourse = () => {
    navigate("/new-book-course", {
      state: { course: lesson?.course },
    });
  };

  const GoToDetailsButton = () => {
    return (
      <CardFooterButton
        role="button"
        tabIndex={0}
        className="card-footer"
        gray={gray}
      >
        <Link style={{ width: "100%" }} to={`/class-details/${lesson.id}`}>
          {t("CLASS DETAILS")}
        </Link>
      </CardFooterButton>
    );
  };

  const AddToCalendarButton = () => {
    const handleClick = () => {
      showAddToCalendarModal();
    };

    return (
      <CardFooterButton
        role="button"
        tabIndex={0}
        className="card-footer"
        gray={gray}
        onClick={() => handleClick()}
      >
        {t("ADD TO CALENDAR")}
      </CardFooterButton>
    );
  };

  const PartOfCourse = () => {
    return (
      <CardFooterButton
        role="button"
        tabIndex={0}
        className="card-footer"
        gray={gray}
        onClick={navigateToCourse}
      >
        {t("SHOW COURSE #")}
        {lesson?.course?.id}
      </CardFooterButton>
    );
  };

  const renderFooterButton = () => {
    // If this is just a basic card, CTA always is going to class details
    // Or class is not booked yet
    if (!lesson.bookedAt) return GoToDetailsButton();

    // If class is booked but
    if (lesson.bookedAt && appointment.status === "pending")
      return AddToCalendarButton();

    if (lesson.bookedAt && appointment.status === "completed") {
      return PartOfCourse();
    }

    return null;
  };

  return (
    <>
      {renderFooterButton()}
      <AddToCalendarModal
        visible={visibleAddToCalendarModal}
        onCloseModal={closeAddToCalendarModal}
        webcalUrl={webcalUrl}
      />
    </>
  );
};

type TNormalCardFooterProps = {
  hidesFooter: boolean;
  lesson: Lesson;
  gray?: boolean;
};
const NormalCardFooter = ({
  hidesFooter,
  lesson,
  gray,
}: TNormalCardFooterProps) => {
  const { t } = useTranslation("my_classes");

  if (hidesFooter) return null;

  return (
    <CardFooterButton
      role="button"
      tabIndex={0}
      className="card-footer"
      gray={gray}
    >
      <Link style={{ width: "100%" }} to={`/class-details/${lesson.id}`}>
        {t("CLASS DETAILS")}
      </Link>
    </CardFooterButton>
  );
};

type CardFooterProps = {
  lesson: Lesson;
  isStacked: boolean;
  onNextCardInStack: () => void;
  onPrevCardInStack: () => void;
  hidesFooter: boolean;
  dynamicFooter: boolean;
  gray?: boolean;
  section?: string;
};
const ClassCardFooter = ({
  lesson,
  gray,
  hidesFooter,
  dynamicFooter,
  isStacked,
  onPrevCardInStack,
  onNextCardInStack,
  section,
}: CardFooterProps) => {
  if (hidesFooter) return null;

  const FooterButton = dynamicFooter ? (
    <DynamicCardFooter {...{ gray, lesson }} />
  ) : (
    <NormalCardFooter {...{ gray, hidesFooter, lesson }} />
  );

  const renderCardFooter = () => {
    // if classes are not stacked, display just the appropriate text, no arrows
    if (!isStacked) return FooterButton;

    // If classes are stacked, put 2 navigating arrows on both sides of the footer
    return (
      <StackedCardFooterContainer>
        <CardFooterNavigationArrow
          icon={faArrowLeft as IconProp}
          onClick={onPrevCardInStack}
          gray={gray ? 1 : 0}
        />
        {FooterButton}
        <CardFooterNavigationArrow
          icon={faArrowRight as IconProp}
          onClick={onNextCardInStack}
          gray={gray ? 1 : 0}
        />
      </StackedCardFooterContainer>
    );
  };

  // If it's the filter result in Book A Class
  // and class are 1 on 1 then don't show footer
  if (section === "filter-result" && lesson.classType === "1 on 1") return null;

  return <ClassCardFooterWrapper>{renderCardFooter()}</ClassCardFooterWrapper>;
};

export default ClassCardFooter;
